import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Users,
  User,
  FileText,
  ChevronRight,
  ChevronDown,
  Plus,
  Edit2,
  Gavel,
  Upload,
  CheckCircle,
  Inbox,
} from 'lucide-react';
import { buildTourDemoCommittees, buildTourDemoOrgUsers } from '../tours/tourDemoData';
import { hasSupabaseConfig } from '../lib/supabaseClient';
import {
  listOrgLoginUsers,
  saveCommitteeCloud,
  updateCommitteeReferral,
  startCommitteeMeeting,
  saveCommitteeMeetingState,
  saveCommitteeAttendance,
  listCommitteeDocuments,
  uploadCommitteeDocument,
  getCommitteeDocumentUrl,
} from '../db/sync/committeesSync';
import { listOrgRoster } from '../db/sync/rosterSync';

const dispositionLabel = (d) => {
  if (d === 'favorable') return 'favorably';
  if (d === 'unfavorable') return 'unfavorably';
  return 'with no recommendation';
};

const Committees = ({
  theme = 'light',
  committees = [],
  setCommittees,
  onReloadCommittees = null,
  organizationId = null,
  currentUserId = null,
  canManageCommittees = true,
  onAddAgendaEntry,
  meetingMinutes = [],
  setMeetingMinutes,
  organizationName = '',
}) => {
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [expandedCommittees, setExpandedCommittees] = useState(new Set());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [orgUsers, setOrgUsers] = useState([]);
  const [rosterPeople, setRosterPeople] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [newCommittee, setNewCommittee] = useState({
    name: '',
    description: '',
    chairUserId: '',
    memberUserIds: [],
  });
  const [editingCommittee, setEditingCommittee] = useState(null);
  const [showCommitteeMeeting, setShowCommitteeMeeting] = useState(false);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [meetingNotes, setMeetingNotes] = useState('');
  const [minuteDraft, setMinuteDraft] = useState('');
  const [meetingMinutesLocal, setMeetingMinutesLocal] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [documents, setDocuments] = useState([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [reportDraft, setReportDraft] = useState({});
  const [tourDemoCommittees, setTourDemoCommittees] = useState(null);

  // tourDemoCommittees, once set by a running tour, always wins — a tour must never
  // show the org's real committees/chairs/emails just because some happen to exist.
  const displayCommittees = tourDemoCommittees || committees;

  const myCommittees = useMemo(() => {
    if (!currentUserId) return displayCommittees;
    return displayCommittees.filter((c) =>
      (c.memberships || []).some((m) => m.userId === currentUserId)
    );
  }, [displayCommittees, currentUserId]);

  const inboxItems = useMemo(() => {
    const source = currentUserId && !canManageCommittees ? myCommittees : displayCommittees;
    const items = [];
    source.forEach((c) => {
      (c.referredItems || [])
        .filter((r) => r.status === 'pending' || r.status === 'in_progress')
        .forEach((r) => items.push({ ...r, committeeId: c.id, committeeName: c.name }));
    });
    return items;
  }, [displayCommittees, myCommittees, currentUserId, canManageCommittees]);

  const cardClass =
    theme === 'dark'
      ? 'bg-gray-800 text-white border border-gray-600'
      : 'bg-pp-surface text-pp-ink border border-pp-line/80';
  const inputClass =
    theme === 'dark'
      ? 'bg-gray-700 border-gray-600 text-white rounded-lg'
      : 'border-pp-line bg-pp-surface text-pp-ink rounded-lg';

  useEffect(() => {
    if (!organizationId || !hasSupabaseConfig()) {
      setOrgUsers([]);
      setRosterPeople([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const [users, roster] = await Promise.all([
          listOrgLoginUsers(organizationId),
          listOrgRoster(organizationId, { activeOnly: true }),
        ]);
        if (cancelled) return;
        const rosterByUser = new Map(
          (roster || []).filter((r) => r.userId).map((r) => [r.userId, r])
        );
        const enriched = (users || []).map((u) => {
          const r = rosterByUser.get(u.userId);
          const displayName = r?.displayName || u.displayName || u.email || 'User';
          return { ...u, displayName, rosterId: r?.id || null, hasLogin: true };
        });
        const linkedIds = new Set(enriched.map((u) => u.userId));
        const unlinked = (roster || [])
          .filter((r) => !r.userId)
          .filter((r) => !linkedIds.has(r.userId))
          .map((r) => ({
            userId: null,
            rosterId: r.id,
            displayName: r.displayName,
            email: r.email || '',
            hasLogin: false,
          }));
        setOrgUsers(enriched);
        setRosterPeople([...enriched, ...unlinked]);
      } catch (err) {
        console.warn('[Committees] listOrgLoginUsers failed', err?.message || err);
        if (!cancelled) {
          setOrgUsers([]);
          setRosterPeople([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => {
    const openCreate = () => {
      setSelectedCommittee(null);
      setNewCommittee({
        name: 'Program Committee',
        description: 'Plans educational programs and events for the membership.',
        chairUserId: '',
        memberUserIds: [],
      });
      // Never show the real org roster (or the signed-in account's own identity) in a tour.
      const demoUsers = buildTourDemoOrgUsers();
      setOrgUsers(demoUsers);
      setRosterPeople(demoUsers);
      setShowCreateForm(true);
    };
    const prepareCards = () => {
      setSelectedCommittee(null);
      setShowCreateForm(false);
      setShowEditForm(false);
      // Always demo data here — a tour must never show the org's real committees,
      // regardless of whether any already exist.
      const demoCommittees = buildTourDemoCommittees();
      setTourDemoCommittees(demoCommittees);
      const firstId = demoCommittees[0]?.id;
      if (firstId != null) {
        setExpandedCommittees(new Set([firstId]));
      }
    };
    const selectFirst = () => {
      setShowCreateForm(false);
      setShowEditForm(false);
      const demoCommittees = buildTourDemoCommittees();
      setTourDemoCommittees(demoCommittees);
      if (demoCommittees.length > 0) {
        setSelectedCommittee(demoCommittees[0]);
      }
    };
    const openCommitteeMeeting = () => {
      setShowCreateForm(false);
      setShowEditForm(false);
      const demoCommittees = buildTourDemoCommittees();
      setTourDemoCommittees(demoCommittees);
      const committee = demoCommittees[0];
      if (!committee) return;
      setSelectedCommittee(committee);
      {
        // Demo committee ids aren't real UUIDs — never pass them to a real Supabase
        // call (startCommitteeMeeting/listCommitteeDocuments). Set the mini-session
        // state directly instead, mirroring what openMiniMeeting does locally.
        setError(null);
        setActiveMeeting({
          id: `pp-tour-meeting-${committee.id}`,
          organizationId,
          committeeId: committee.id,
          startedAt: new Date().toISOString(),
          endedAt: null,
          minutes: [],
          notes: '',
          attendance: [],
        });
        setMeetingNotes('');
        setMeetingMinutesLocal([
          {
            id: Date.now(),
            text: `Call to order — ${committee.name}`,
            at: new Date().toLocaleTimeString(),
          },
        ]);
        const att = {};
        (committee.memberships || []).forEach((m) => {
          att[m.userId] = 'present';
        });
        setAttendance(att);
        setDocuments([]);
        setShowCommitteeMeeting(true);
      }
    };
    window.addEventListener('pp-tour-open-committee-create', openCreate);
    window.addEventListener('pp-tour-prepare-committee-cards', prepareCards);
    window.addEventListener('pp-tour-select-first-committee', selectFirst);
    window.addEventListener('pp-tour-open-committee-meeting', openCommitteeMeeting);
    return () => {
      window.removeEventListener('pp-tour-open-committee-create', openCreate);
      window.removeEventListener('pp-tour-prepare-committee-cards', prepareCards);
      window.removeEventListener('pp-tour-select-first-committee', selectFirst);
      window.removeEventListener('pp-tour-open-committee-meeting', openCommitteeMeeting);
    };
  }, [committees]);

  const userLabel = useCallback(
    (userId) => {
      const u = orgUsers.find((x) => x.userId === userId);
      return u?.displayName || u?.email || 'User';
    },
    [orgUsers]
  );

  const buildMemberships = (chairUserId, memberUserIds) => {
    const ids = new Set([...(memberUserIds || [])].filter(Boolean));
    if (chairUserId) ids.add(chairUserId);
    return [...ids].map((userId) => ({
      userId,
      role: userId === chairUserId ? 'chair' : 'member',
      displayName: userLabel(userId),
    }));
  };

  const persistCommittee = async (committee) => {
    if (hasSupabaseConfig() && organizationId) {
      const saved = await saveCommitteeCloud(organizationId, committee);
      if (typeof onReloadCommittees === 'function') {
        await onReloadCommittees();
      } else {
        setCommittees((prev) => {
          const others = prev.filter((c) => c.id !== saved.id);
          return [...others, saved];
        });
      }
      return saved;
    }
    setCommittees((prev) => {
      const exists = prev.some((c) => c.id === committee.id);
      if (exists) return prev.map((c) => (c.id === committee.id ? committee : c));
      return [...prev, committee];
    });
    return committee;
  };

  const createCommittee = async () => {
    if (!newCommittee.name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const memberships = buildMemberships(newCommittee.chairUserId, newCommittee.memberUserIds);
      const committee = {
        id: hasSupabaseConfig() ? undefined : Date.now(),
        name: newCommittee.name.trim(),
        type: 'standing',
        description: newCommittee.description.trim(),
        chairUserId: newCommittee.chairUserId || null,
        chair: newCommittee.chairUserId ? userLabel(newCommittee.chairUserId) : '',
        memberships,
        members: memberships.map((m) => m.displayName),
        referredItems: [],
        created: new Date().toLocaleDateString(),
      };
      const saved = await persistCommittee(committee);
      setSelectedCommittee(saved);
      setNewCommittee({ name: '', description: '', chairUserId: '', memberUserIds: [] });
      setShowCreateForm(false);
      setTourDemoCommittees(null);
    } catch (err) {
      setError(err.message || 'Could not create committee');
    } finally {
      setBusy(false);
    }
  };

  const updateCommittee = async () => {
    if (!editingCommittee || !editingCommittee.name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const memberships = buildMemberships(
        editingCommittee.chairUserId,
        (editingCommittee.memberships || []).map((m) => m.userId)
      );
      const updated = {
        ...editingCommittee,
        name: editingCommittee.name.trim(),
        description: editingCommittee.description || '',
        chairUserId: editingCommittee.chairUserId || null,
        chair: editingCommittee.chairUserId ? userLabel(editingCommittee.chairUserId) : '',
        memberships,
        members: memberships.map((m) => m.displayName),
      };
      const saved = await persistCommittee(updated);
      setSelectedCommittee(saved);
      setShowEditForm(false);
      setEditingCommittee(null);
    } catch (err) {
      setError(err.message || 'Could not update committee');
    } finally {
      setBusy(false);
    }
  };

  const toggleExpanded = (committeeId) => {
    const next = new Set(expandedCommittees);
    if (next.has(committeeId)) next.delete(committeeId);
    else next.add(committeeId);
    setExpandedCommittees(next);
  };

  const getPendingItems = (committee) =>
    (committee.referredItems || []).filter(
      (item) => item.status === 'pending' || item.status === 'in_progress'
    );

  const getCompletedItems = (committee) =>
    (committee.referredItems || []).filter((item) => item.status === 'completed');

  const patchReferralLocal = (committeeId, referralId, patch) => {
    setCommittees((prev) =>
      prev.map((c) => {
        if (c.id !== committeeId) return c;
        return {
          ...c,
          referredItems: (c.referredItems || []).map((r) =>
            r.id === referralId ? { ...r, ...patch } : r
          ),
        };
      })
    );
    setSelectedCommittee((prev) => {
      if (!prev || prev.id !== committeeId) return prev;
      return {
        ...prev,
        referredItems: (prev.referredItems || []).map((r) =>
          r.id === referralId ? { ...r, ...patch } : r
        ),
      };
    });
  };

  const completeCommitteeItem = async (item, committee, disposition) => {
    const recommendations = reportDraft[item.id]?.recommendations || item.recommendations || '';
    const patch = {
      status: 'completed',
      disposition,
      recommendations,
      dateCompleted: new Date().toLocaleDateString(),
      completedBy: committee.chair || 'Committee',
    };
    patchReferralLocal(committee.id, item.id, patch);
    if (hasSupabaseConfig() && typeof item.id === 'string') {
      try {
        await updateCommitteeReferral(item.id, {
          status: 'completed',
          disposition: disposition === 'no_action' ? 'no_recommendation' : disposition,
          recommendations,
        });
      } catch (err) {
        console.warn('[Committees] update referral failed', err?.message || err);
      }
    }
  };

  const reportToAssembly = async (item, committee) => {
    const disposition = item.disposition || 'no_recommendation';
    const recommendations =
      reportDraft[item.id]?.recommendations || item.recommendations || '';
    const reportText = `The ${committee.name} reports ${dispositionLabel(disposition)} on "${item.title}".`;
    const recSuffix = recommendations.trim()
      ? ` Additional recommendations: ${recommendations.trim()}`
      : '';

    if (onAddAgendaEntry) {
      onAddAgendaEntry({
        id: Date.now(),
        text: `Committee Report: ${reportText}${recSuffix}`,
        timestamp: new Date().toLocaleTimeString(),
        type: 'committee_report',
        committee: committee.name,
        disposition,
        recommendations,
        originalMotionId: item.motionId,
      });
    }

    const patch = {
      reportedToAssembly: true,
      reportedDate: new Date().toLocaleDateString(),
      recommendations,
    };
    patchReferralLocal(committee.id, item.id, patch);

    if (hasSupabaseConfig() && typeof item.id === 'string') {
      try {
        await updateCommitteeReferral(item.id, {
          reportedToAssembly: true,
          recommendations,
          disposition:
            disposition === 'no_action' ? 'no_recommendation' : disposition,
        });
      } catch (err) {
        console.warn('[Committees] report referral failed', err?.message || err);
      }
    }

    alert(`Committee report added to main meeting agenda:\n\n${reportText}${recSuffix}`);
  };

  const openMiniMeeting = async (committee) => {
    setError(null);
    setBusy(true);
    try {
      const meeting = await startCommitteeMeeting({
        organizationId,
        committeeId: committee.id,
        createdBy: currentUserId,
      });
      setActiveMeeting(meeting);
      setMeetingNotes('');
      setMeetingMinutesLocal([
        {
          id: Date.now(),
          text: `Call to order — ${committee.name}`,
          at: new Date().toLocaleTimeString(),
        },
      ]);
      const att = {};
      (committee.memberships || []).forEach((m) => {
        att[m.userId] = 'present';
      });
      setAttendance(att);
      setShowCommitteeMeeting(true);
      if (hasSupabaseConfig()) {
        const docs = await listCommitteeDocuments(committee.id);
        setDocuments(docs);
      } else {
        setDocuments([]);
      }
    } catch (err) {
      setError(err.message || 'Could not start committee meeting');
    } finally {
      setBusy(false);
    }
  };

  const addMinuteLine = () => {
    if (!minuteDraft.trim()) return;
    setMeetingMinutesLocal((prev) => [
      ...prev,
      { id: Date.now(), text: minuteDraft.trim(), at: new Date().toLocaleTimeString() },
    ]);
    setMinuteDraft('');
  };

  const saveAndEndMeeting = async (committee) => {
    if (!activeMeeting) return;
    setBusy(true);
    setError(null);
    try {
      const attendanceRows = Object.entries(attendance).map(([userId, status]) => ({
        userId,
        status,
      }));
      if (hasSupabaseConfig() && typeof activeMeeting.id === 'string') {
        await saveCommitteeAttendance(activeMeeting.id, organizationId, attendanceRows);
        await saveCommitteeMeetingState(activeMeeting.id, {
          notes: meetingNotes,
          minutes: meetingMinutesLocal,
          endedAt: new Date().toISOString(),
        });
      }

      const presentCount = attendanceRows.filter((r) => r.status === 'present').length;
      const blob = {
        id: Date.now(),
        type: 'committee',
        organizationName,
        meetingType: `${committee.name} Committee Meeting`,
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString(),
        text: [
          `Committee meeting of the ${committee.name}.`,
          `Attendance: ${presentCount} present.`,
          ...meetingMinutesLocal.map((m) => m.text),
          meetingNotes.trim() ? `Notes: ${meetingNotes.trim()}` : null,
        ]
          .filter(Boolean)
          .join(' '),
        timestamp: new Date().toLocaleTimeString(),
        committeeId: committee.id,
        minutes: meetingMinutesLocal,
        notes: meetingNotes,
      };
      if (typeof setMeetingMinutes === 'function') {
        setMeetingMinutes([...(meetingMinutes || []), blob]);
      }
      setShowCommitteeMeeting(false);
      setActiveMeeting(null);
    } catch (err) {
      setError(err.message || 'Could not save meeting');
    } finally {
      setBusy(false);
    }
  };

  const onUploadDoc = async (committee, file, referralId = null) => {
    if (!file || !hasSupabaseConfig()) return;
    setUploadBusy(true);
    setError(null);
    try {
      const row = await uploadCommitteeDocument({
        organizationId,
        committeeId: committee.id,
        file,
        meetingId: activeMeeting?.id,
        referralId,
      });
      setDocuments((prev) => [row, ...prev]);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploadBusy(false);
    }
  };

  const openDoc = async (doc) => {
    try {
      const url = await getCommitteeDocumentUrl(doc.storage_path);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message || 'Could not open document');
    }
  };

  const MembershipPicker = ({ chairUserId, memberUserIds, onChairChange, onToggleMember }) => (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-sm font-medium">Chair</label>
        <select
          value={chairUserId || ''}
          onChange={(e) => onChairChange(e.target.value)}
          className={`w-full border p-2 ${inputClass}`}
        >
          <option value="">Select chair…</option>
          {orgUsers.map((u) => (
            <option key={u.userId} value={u.userId}>
              {u.displayName}
            </option>
          ))}
        </select>
        {!hasSupabaseConfig() && (
          <p className="mt-1 text-xs text-pp-muted">
            Sign in with Supabase to pick org users. Local demo uses empty memberships.
          </p>
        )}
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium">Members</label>
        <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-pp-line/60 p-2">
          {rosterPeople.length === 0 ? (
            <p className="text-sm text-pp-muted">No roster or org users loaded.</p>
          ) : (
            rosterPeople.map((u) => {
              const selectable = Boolean(u.userId);
              const checked =
                selectable &&
                ((memberUserIds || []).includes(u.userId) || u.userId === chairUserId);
              return (
                <label
                  key={u.userId || u.rosterId}
                  className={`flex items-center gap-2 text-sm ${
                    selectable ? '' : 'opacity-60'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!selectable || u.userId === chairUserId}
                    onChange={() => selectable && onToggleMember(u.userId)}
                  />
                  <span>
                    {u.displayName}
                    {u.userId === chairUserId ? ' (chair)' : ''}
                    {!selectable ? ' — needs login to assign' : ''}
                  </span>
                </label>
              );
            })
          )}
        </div>
      </div>
    </div>
  );

  const renderCommitteeDetail = (committee) => {
    const pendingItems = getPendingItems(committee);
    const completedItems = getCompletedItems(committee);
    const seats = committee.memberships || [];

    return (
      <div data-tour="committees-detail">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => {
              setSelectedCommittee(null);
              setShowCommitteeMeeting(false);
            }}
            className="text-sm text-pp-accent hover:underline"
          >
            ← Back to committees
          </button>
          <div className="flex flex-wrap gap-2">
            {canManageCommittees && (
              <button
                type="button"
                onClick={() => {
                  setEditingCommittee({
                    ...committee,
                    chairUserId: committee.chairUserId || '',
                    memberships: seats,
                  });
                  setShowEditForm(true);
                }}
                className="inline-flex items-center gap-1 rounded-lg border border-pp-line px-3 py-1.5 text-sm"
              >
                <Edit2 size={14} /> Edit
              </button>
            )}
            <button
              type="button"
              data-tour="committees-hold-meeting"
              onClick={() => void openMiniMeeting(committee)}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-lg bg-pp-sage px-3 py-1.5 text-sm text-white hover:opacity-90"
            >
              <Gavel size={14} /> Hold committee meeting
            </button>
          </div>
        </div>

        <div className={`${cardClass} mb-6 rounded-xl p-6 shadow-pp`}>
          <h1 className="text-2xl font-semibold tracking-tight">{committee.name}</h1>
          <p className="mt-1 text-sm text-pp-muted">{committee.description || 'No description'}</p>
          <div className="mt-3 flex flex-wrap gap-4 text-sm">
            <span className="inline-flex items-center gap-1">
              <User size={14} /> Chair: {committee.chair || 'Unassigned'}
            </span>
            <span className="inline-flex items-center gap-1">
              <Users size={14} /> {seats.length || committee.members?.length || 0} members
            </span>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}

        {showCommitteeMeeting && activeMeeting && (
          <div className={`${cardClass} mb-6 rounded-xl p-6 shadow-pp`} data-tour="committees-meeting-panel">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Committee meeting</h2>
              <span className="text-xs text-pp-muted">
                Started {new Date(activeMeeting.startedAt).toLocaleTimeString()}
              </span>
            </div>

            <div className="mb-6">
              <h3 className="mb-2 font-medium">Attendance</h3>
              <div className="space-y-2">
                {seats.length === 0 ? (
                  <p className="text-sm text-pp-muted">No members assigned to this committee.</p>
                ) : (
                  seats.map((m) => (
                    <div key={m.userId} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span>
                        {m.displayName}
                        {m.role === 'chair' ? ' (chair)' : ''}
                      </span>
                      <select
                        value={attendance[m.userId] || 'present'}
                        onChange={(e) =>
                          setAttendance((prev) => ({ ...prev, [m.userId]: e.target.value }))
                        }
                        className={`border px-2 py-1 ${inputClass}`}
                      >
                        <option value="present">Present</option>
                        <option value="absent">Absent</option>
                        <option value="excused">Excused</option>
                      </select>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="mb-2 font-medium">Minutes</h3>
              <ul className="mb-2 space-y-1 text-sm">
                {meetingMinutesLocal.map((m) => (
                  <li key={m.id} className="rounded bg-pp-canvas/60 px-2 py-1">
                    <span className="text-xs text-pp-muted">{m.at}</span> — {m.text}
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <input
                  value={minuteDraft}
                  onChange={(e) => setMinuteDraft(e.target.value)}
                  className={`flex-1 border p-2 text-sm ${inputClass}`}
                  placeholder="Add a minute entry…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') addMinuteLine();
                  }}
                />
                <button
                  type="button"
                  onClick={addMinuteLine}
                  className="rounded-lg bg-pp-accent px-3 py-1.5 text-sm text-white"
                >
                  Add
                </button>
              </div>
              <textarea
                value={meetingNotes}
                onChange={(e) => setMeetingNotes(e.target.value)}
                className={`mt-3 w-full border p-3 text-sm ${inputClass}`}
                rows={4}
                placeholder="Additional discussion notes…"
              />
            </div>

            <div className="mb-6">
              <h3 className="mb-2 font-medium">Documents / work product</h3>
              {hasSupabaseConfig() ? (
                <div className="space-y-2">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-pp-line px-3 py-2 text-sm">
                    <Upload size={14} />
                    {uploadBusy ? 'Uploading…' : 'Upload file'}
                    <input
                      type="file"
                      className="hidden"
                      disabled={uploadBusy}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void onUploadDoc(committee, f);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <ul className="space-y-1 text-sm">
                    {documents.map((d) => (
                      <li key={d.id}>
                        <button
                          type="button"
                          className="text-pp-accent hover:underline"
                          onClick={() => void openDoc(d)}
                        >
                          {d.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p className="text-sm text-pp-muted">Document upload requires Supabase.</p>
              )}
            </div>

            <div className="mb-4" data-tour="committees-meeting-disposition">
              <h3 className="mb-2 font-medium">Pending referrals — disposition</h3>
              {pendingItems.length === 0 ? (
                <p className="text-sm text-pp-muted">No pending referrals.</p>
              ) : (
                <div className="space-y-3">
                  {pendingItems.map((item) => (
                    <div key={item.id} className="rounded-lg border border-pp-line/70 p-3">
                      <p className="font-medium">{item.title}</p>
                      <p className="text-xs text-pp-muted">
                        Instructions: {item.instructions || 'None'}
                      </p>
                      <textarea
                        className={`mt-2 w-full border p-2 text-sm ${inputClass}`}
                        rows={2}
                        placeholder="Additional recommendations to the assembly…"
                        value={reportDraft[item.id]?.recommendations || item.recommendations || ''}
                        onChange={(e) =>
                          setReportDraft((prev) => ({
                            ...prev,
                            [item.id]: { recommendations: e.target.value },
                          }))
                        }
                      />
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => void completeCommitteeItem(item, committee, 'favorable')}
                          className="rounded bg-green-700 px-2 py-1 text-xs text-white"
                        >
                          Favorable
                        </button>
                        <button
                          type="button"
                          onClick={() => void completeCommitteeItem(item, committee, 'unfavorable')}
                          className="rounded bg-red-700 px-2 py-1 text-xs text-white"
                        >
                          Unfavorable
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            void completeCommitteeItem(item, committee, 'no_recommendation')
                          }
                          className="rounded bg-gray-600 px-2 py-1 text-xs text-white"
                        >
                          No recommendation
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => void saveAndEndMeeting(committee)}
                className="rounded-lg bg-pp-accent px-4 py-2 text-sm text-white"
              >
                Save &amp; end meeting
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCommitteeMeeting(false);
                  setActiveMeeting(null);
                }}
                className="rounded-lg border border-pp-line px-4 py-2 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="mb-6 grid gap-6 lg:grid-cols-2">
          <div className={`${cardClass} rounded-xl p-6 shadow-pp`}>
            <h2 className="mb-3 text-lg font-semibold">Pending business</h2>
            {pendingItems.length === 0 ? (
              <p className="text-sm text-pp-muted">Nothing assigned.</p>
            ) : (
              <div className="space-y-3">
                {pendingItems.map((item) => (
                  <div key={item.id} className="rounded-lg border border-pp-line/70 p-3">
                    <p className="font-medium">{item.title}</p>
                    <p className="text-xs text-pp-muted">
                      Referred {item.dateReferred} · {item.instructions}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className={`${cardClass} rounded-xl p-6 shadow-pp`}>
            <h2 className="mb-3 text-lg font-semibold">Completed — report to assembly</h2>
            {completedItems.length === 0 ? (
              <p className="text-sm text-pp-muted">No completed items yet.</p>
            ) : (
              <div className="space-y-3">
                {completedItems.map((item) => (
                  <div key={item.id} className="rounded-lg border border-pp-line/70 p-3">
                    <p className="font-medium">{item.title}</p>
                    <p className="text-xs text-pp-muted">
                      Disposition: {item.disposition || '—'}
                      {item.recommendations ? ` · ${item.recommendations}` : ''}
                    </p>
                    {!item.reportedToAssembly ? (
                      <button
                        type="button"
                        onClick={() => void reportToAssembly(item, committee)}
                        className="mt-2 inline-flex items-center gap-1 rounded-lg bg-pp-accent px-3 py-1.5 text-xs text-white"
                      >
                        <CheckCircle size={12} /> Report to assembly
                      </button>
                    ) : (
                      <p className="mt-1 text-xs text-green-700">
                        Reported {item.reportedDate || ''}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={`${cardClass} rounded-xl p-6 shadow-pp`}>
          <h2 className="mb-3 text-lg font-semibold">Committee members</h2>
          {seats.length > 0 ? (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {seats.map((m) => (
                <div key={m.userId} className="rounded-lg border border-pp-line/60 p-3 text-sm">
                  <p className="font-medium">{m.displayName}</p>
                  {m.role === 'chair' && (
                    <span className="mt-1 inline-block rounded bg-pp-accentSoft px-2 py-0.5 text-xs text-pp-accent">
                      Chair
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-pp-muted">
              {(committee.members || []).length
                ? (committee.members || []).join(', ')
                : 'No members assigned yet'}
            </p>
          )}
        </div>
      </div>
    );
  };

  if (selectedCommittee) {
    const live =
      committees.find((c) => c.id === selectedCommittee.id) || selectedCommittee;
    if (showEditForm && editingCommittee) {
      return (
        <div className="mx-auto max-w-2xl p-4">
          <div className={`${cardClass} rounded-xl p-6 shadow-pp`}>
            <h2 className="mb-4 text-xl font-semibold">Edit committee</h2>
            {error && <p className="mb-2 text-sm text-red-700">{error}</p>}
            <div className="mb-3">
              <label className="mb-1 block text-sm font-medium">Name</label>
              <input
                value={editingCommittee.name}
                onChange={(e) =>
                  setEditingCommittee({ ...editingCommittee, name: e.target.value })
                }
                className={`w-full border p-2 ${inputClass}`}
              />
            </div>
            <div className="mb-3">
              <label className="mb-1 block text-sm font-medium">Description</label>
              <textarea
                value={editingCommittee.description || ''}
                onChange={(e) =>
                  setEditingCommittee({ ...editingCommittee, description: e.target.value })
                }
                className={`w-full border p-2 ${inputClass}`}
                rows={3}
              />
            </div>
            <MembershipPicker
              chairUserId={editingCommittee.chairUserId}
              memberUserIds={(editingCommittee.memberships || []).map((m) => m.userId)}
              onChairChange={(id) =>
                setEditingCommittee((prev) => ({
                  ...prev,
                  chairUserId: id,
                  memberships: buildMemberships(
                    id,
                    (prev.memberships || []).map((m) => m.userId)
                  ),
                }))
              }
              onToggleMember={(userId) => {
                setEditingCommittee((prev) => {
                  const ids = new Set((prev.memberships || []).map((m) => m.userId));
                  if (ids.has(userId)) ids.delete(userId);
                  else ids.add(userId);
                  return {
                    ...prev,
                    memberships: buildMemberships(prev.chairUserId, [...ids]),
                  };
                });
              }}
            />
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                disabled={busy}
                onClick={() => void updateCommittee()}
                className="rounded-lg bg-pp-accent px-4 py-2 text-sm text-white"
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowEditForm(false);
                  setEditingCommittee(null);
                }}
                className="rounded-lg border border-pp-line px-4 py-2 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      );
    }
    return <div className="mx-auto max-w-5xl p-4">{renderCommitteeDetail(live)}</div>;
  }

  return (
    <div className="mx-auto max-w-5xl p-4">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 data-tour="committees-header" className="text-2xl font-semibold tracking-tight">
            Committees
          </h1>
          <p className="text-sm text-pp-muted">
            Assigned referrals, mini-meetings, and reports to the assembly.
          </p>
        </div>
        {canManageCommittees && (
          <button
            data-tour="committees-new"
            type="button"
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-1 rounded-lg bg-pp-sage px-4 py-2 text-sm text-white"
          >
            <Plus size={16} /> New committee
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Inbox */}
      <div className={`${cardClass} mb-6 rounded-xl p-5 shadow-pp`} data-tour="committees-inbox">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
          <Inbox size={18} /> Assigned to me
        </h2>
        {inboxItems.length === 0 ? (
          <p className="text-sm text-pp-muted">No pending referrals on your committees.</p>
        ) : (
          <ul className="space-y-2">
            {inboxItems.map((item) => (
              <li key={`${item.committeeId}-${item.id}`}>
                <button
                  type="button"
                  className="w-full rounded-lg border border-pp-line/70 p-3 text-left hover:bg-pp-canvas/50"
                  onClick={() => {
                    const c = displayCommittees.find((x) => x.id === item.committeeId);
                    if (c) setSelectedCommittee(c);
                  }}
                >
                  <p className="font-medium">{item.title}</p>
                  <p className="text-xs text-pp-muted">
                    {item.committeeName} · referred {item.dateReferred}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {showCreateForm && (
        <div
          data-tour="committees-create-form"
          className={`${cardClass} mb-6 rounded-xl p-6 shadow-pp`}
        >
          <h2 className="mb-4 text-xl font-semibold">Create committee</h2>
          <div className="mb-3">
            <label className="mb-1 block text-sm font-medium">Name</label>
            <input
              data-tour="committees-create-name"
              value={newCommittee.name}
              onChange={(e) => setNewCommittee({ ...newCommittee, name: e.target.value })}
              className={`w-full border p-2 ${inputClass}`}
            />
          </div>
          <div className="mb-3">
            <label className="mb-1 block text-sm font-medium">Description</label>
            <textarea
              value={newCommittee.description}
              onChange={(e) =>
                setNewCommittee({ ...newCommittee, description: e.target.value })
              }
              className={`w-full border p-2 ${inputClass}`}
              rows={3}
            />
          </div>
          <div data-tour="committees-create-members">
            <MembershipPicker
              chairUserId={newCommittee.chairUserId}
              memberUserIds={newCommittee.memberUserIds}
              onChairChange={(id) =>
                setNewCommittee((prev) => ({
                  ...prev,
                  chairUserId: id,
                  memberUserIds: prev.memberUserIds.includes(id)
                    ? prev.memberUserIds
                    : [...prev.memberUserIds, id].filter(Boolean),
                }))
              }
              onToggleMember={(userId) => {
                setNewCommittee((prev) => {
                  const ids = new Set(prev.memberUserIds);
                  if (ids.has(userId)) ids.delete(userId);
                  else ids.add(userId);
                  return { ...prev, memberUserIds: [...ids] };
                });
              }}
            />
          </div>
          <div className="mt-4 flex gap-2">
            <button
              data-tour="committees-create-submit"
              type="button"
              disabled={busy}
              onClick={() => void createCommittee()}
              className="rounded-lg bg-pp-accent px-4 py-2 text-sm text-white"
            >
              Create
            </button>
            <button
              type="button"
              onClick={() => setShowCreateForm(false)}
              className="rounded-lg border border-pp-line px-4 py-2 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-4" data-tour="committees-list">
        {(canManageCommittees ? displayCommittees : myCommittees).map((committee, idx) => {
          const isFirst = idx === 0;
          const isExpanded = expandedCommittees.has(committee.id);
          const pendingItems = getPendingItems(committee);
          const completedItems = getCompletedItems(committee);
          const seatCount =
            committee.memberships?.length || committee.members?.length || 0;

          return (
            <div key={committee.id} className={`${cardClass} rounded-xl shadow-pp`}>
              <div className="p-4">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      data-tour={isFirst ? 'committees-card-expand' : undefined}
                      type="button"
                      onClick={() => toggleExpanded(committee.id)}
                      className="rounded p-1 hover:bg-black/5"
                    >
                      {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    <h3 className="text-lg font-semibold">{committee.name}</h3>
                  </div>
                  <button
                    data-tour={isFirst ? 'committees-view-details' : undefined}
                    type="button"
                    onClick={() => setSelectedCommittee(committee)}
                    className="rounded-lg bg-pp-accent px-3 py-1 text-sm text-white hover:bg-pp-accentHover"
                  >
                    View details
                  </button>
                </div>
                <p className="mb-3 text-sm text-pp-muted">{committee.description}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <span className="inline-flex items-center gap-1">
                    <User size={14} /> {committee.chair || 'No chair'}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Users size={14} /> {seatCount} members
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <FileText size={14} /> {pendingItems.length} pending
                  </span>
                </div>
                {isExpanded && (
                  <div className="mt-4 border-t border-pp-line/60 pt-4">
                    <div className="mb-4 grid grid-cols-2 gap-4">
                      <div className="rounded bg-pp-canvas/50 p-3">
                        <div className="mb-1 text-xs text-pp-muted">Pending</div>
                        <div className="font-semibold">{pendingItems.length}</div>
                      </div>
                      <div className="rounded bg-pp-canvas/50 p-3">
                        <div className="mb-1 text-xs text-pp-muted">Completed</div>
                        <div className="font-semibold">{completedItems.length}</div>
                      </div>
                    </div>
                    {pendingItems.slice(0, 3).map((item) => (
                      <div key={item.id} className="mb-2 rounded bg-pp-canvas/40 p-2 text-sm">
                        <div className="font-medium">{item.title}</div>
                        <div className="text-xs text-pp-muted">Referred {item.dateReferred}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {(canManageCommittees ? displayCommittees : myCommittees).length === 0 && (
        <div className={`${cardClass} rounded-xl p-8 text-center shadow-pp`}>
          <Users size={48} className="mx-auto mb-4 text-pp-muted" />
          <h3 className="mb-2 text-lg font-semibold">No committees yet</h3>
          <p className="mb-4 text-pp-muted">
            {canManageCommittees
              ? 'Create a standing committee and assign login users as members.'
              : 'You have not been assigned to a committee yet.'}
          </p>
          {canManageCommittees && (
            <button
              type="button"
              onClick={() => setShowCreateForm(true)}
              className="rounded-lg bg-pp-sage px-4 py-2 text-white"
            >
              Create first committee
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Committees;
